'use client';

import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import slugify from 'react-slugify';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { useFinance } from '@/hooks/finance/finance.hook';
import { DealerWithProvince } from '@/services/dealership/dealership.service';
import { getImageLink } from '@/services/link.service';

import Styles from './DealerFinance.module.scss';

import type React from 'react';

interface DealerFinanceClientProps {
  email?: string;
  dealershipId: string;
  dealership: DealerWithProvince;
  salespersonName?: string;
  finance_disclamer?: string | null;
}

interface FormData {
  vehicleDescription: string;
  salesperson: string;
  region: string;
}

const DealerFinanceClient: React.FC<DealerFinanceClientProps> = ({
  email,
  dealershipId,
  dealership,
  salespersonName,
  finance_disclamer,
}) => {
  const router = useRouter();
  const { setSelectedFinance } = useFinance();

  const { register, handleSubmit, formState } = useForm<FormData>({
    defaultValues: {
      vehicleDescription: '',
      salesperson: salespersonName || '',
    },
  });
  const { isSubmitting, isValid } = formState;

  const onSubmit = async (data: FormData) => {
    try {
      const finalSalesperson = data?.salesperson || '';
      const finalEmail = email || '';

      const modelField =
        finalSalesperson && finalEmail
          ? `${finalSalesperson} (${finalEmail})`
          : finalSalesperson || finalEmail || '';

      setSelectedFinance({
        'redirect-url': `${process.env.NEXT_PUBLIC_CLIENT_URL}/dealerships/${slugify(dealership?.name || '')}`,
        'exit-to-vehicles': `${process.env.NEXT_PUBLIC_CLIENT_URL}/dealerships/${slugify(dealership?.name || '')}`,
        dealerRef: dealership?.cms_new_dealer_code || dealershipId,
        dealerFloor: dealership?.cms_new_dealer_code || 'NEWKIA',
        region: dealership?.province?.name,
        vehicleImage:
          getImageLink('3933613d-5167-4d0b-8f6a-b4ca90723019') ?? '',
        make: data.vehicleDescription,
        model: modelField,
        year: 9999,
        price: 99999,
        mmCode: '9999999',
        variant: '',
        used: true,
        mileage: '99999',
        transmission: 'Automatic',
        source: 'KIA.CO.ZA Fin-App-Dealers',
      });

      router.push('/apply-finance');
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Container maxW={'1280'} width={'100%'} className={Styles.vinSearchWrapper}>
      <Box className={Styles.dealerFinanceWrapper}>
        <Container maxW="1200px">
          <Box maxW={{ base: '100%', lg: '750px' }} mb={9}>
            <Box className={Styles.headingElement}>
              <Heading
                as="h2"
                className={Styles.titleXl}
                marginBottom={{ base: 4, md: 6 }}
              >
                Start Your Application
              </Heading>
            </Box>
            <Text>
              Complete the form below to begin your finance journey with{' '}
              {dealership?.name || 'our dealership'}.
            </Text>
          </Box>

          <Box maxWidth={{ base: '100%', lg: '100%' }}>
            <VStack spacing={8} as="form" onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={6} w="full">
                <FormControl isRequired>
                  <FormLabel className={Styles.formLabel}>
                    Vehicle Description
                  </FormLabel>
                  <Textarea
                    {...register('vehicleDescription', {
                      required: 'Vehicle description is required',
                    })}
                    placeholder="e.g., Kia Sportage 2024, Red SUV with leather seats..."
                    rows={4}
                    resize="vertical"
                    className={Styles.formInput}
                    variant="flushed"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel className={Styles.formLabel}>
                    Sales person Name
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder="Enter salesperson name"
                    variant="flushed"
                    {...register('salesperson', {
                      required: 'Salesperson name is required',
                    })}
                    className={Styles.formInput}
                  />
                </FormControl>
                <Stack
                  gap={{ base: 6, lg: 12 }}
                  direction={{ base: 'column', lg: 'row' }}
                  w="full"
                >
                  <ButtonCustom
                    type="submit"
                    extraClass={Styles.ctaBtn}
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? 'Processing...' : 'Apply for Finance'}
                  </ButtonCustom>
                </Stack>
              </VStack>
            </VStack>
            {finance_disclamer ? (
              <Box
                mt={12}
                className={Styles.disclaimerBox}
                dangerouslySetInnerHTML={{
                  __html: finance_disclamer,
                }}
              ></Box>
            ) : null}
          </Box>
        </Container>
      </Box>
    </Container>
  );
};

export default DealerFinanceClient;
